import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { COUNTER_RESULT_TYPE, CounterResultsRequest } from './core/counter-result.model';
import { initialStatus, loadingStatus, successStatus } from '../../../../../core/models/request-status.model';
import { CounterResultService } from './core/counter-result.service';
import { Alert, ALERT_TYPE } from '../../../../../components/alert/alert.model';
import { RoutingService } from '@spartacus/core';

@Component({
  selector: 'counter-results',
  templateUrl: './counter-results.component.html',
  styleUrl: './counter-results.component.scss',
  standalone: false,
})
export class CounterResultsComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  currentTerm = '';
  counterResultRequest$ = new BehaviorSubject<CounterResultsRequest>({ ...initialStatus });
  errorMessage: Alert = {
    isActive: true,
    content: '',
    type: ALERT_TYPE.DANGER,
    hasCloseButton: false,
  };

  returnTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private counterResultService: CounterResultService,
    protected routing: RoutingService,
  ) {}

  ngOnInit(): void {
    this.getCurrentTerm();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getCurrentTerm() {
    this.activeRoute.params.subscribe(params => {
      this.currentTerm = params['query'];
      this.getResults();
    });
  }

  getResults() {
    this.counterResultRequest$.next({ ...loadingStatus });
    const inSearchPages = (this.router.url || '').includes('search-pages');
    const inSearchLiteratures = (this.router.url || '').includes('search-literatures');
    const inAnyContentPage = inSearchPages || inSearchLiteratures;

    this.subscription.add(
      combineLatest([
        this.counterResultService.getQtyProduct(this.currentTerm, inAnyContentPage),
        this.counterResultService.getQtyContent(this.currentTerm, inSearchPages),
        this.counterResultService.getQtyLiterature(this.currentTerm, inSearchLiteratures),
      ]).subscribe(([qtyProducts, qtyContent, qtyLiterature]: any) => {
        qtyProducts = parseInt(qtyProducts);
        qtyContent = parseInt(qtyContent);
        qtyLiterature = parseInt(qtyLiterature);

        const couterResult: CounterResultsRequest = {
          results: [
            {
              label: 'Products',
              path: '/search',
              type: COUNTER_RESULT_TYPE.PRODUCTS,
              qty: qtyProducts,
              disabled: qtyProducts === 0,
              class: inAnyContentPage ? 'btn-outline-primary' : 'btn-primary',
            },
            {
              label: 'Pages',
              path: '/search-pages',
              type: COUNTER_RESULT_TYPE.PAGES,
              qty: qtyContent,
              disabled: qtyContent === 0,
              class: inSearchPages ? 'btn-primary' : 'btn-outline-primary',
            },
            {
              label: 'Literatures',
              path: '/search-literatures',
              type: COUNTER_RESULT_TYPE.LITERATURES,
              qty: qtyLiterature,
              disabled: qtyLiterature === 0,
              class: inSearchLiteratures ? 'btn-primary' : 'btn-outline-primary',
            },
          ],
          ...successStatus,
        };

        this.counterResultRequest$.next(couterResult);

        clearTimeout(this.returnTimeout);

        this.returnTimeout = setTimeout(() => {
          // If user is on product search and there are no products, prefer pages then literatures
          if (!inAnyContentPage && qtyProducts === 0) {
            if (qtyContent > 0) {
              this.routing?.go({ cxRoute: 'searchPage', params: { query: this.currentTerm } });
              return;
            }
            if (qtyLiterature > 0) {
              this.routing?.go({ cxRoute: 'searchLiterature', params: { query: this.currentTerm } });
              return;
            }
          }

          // If on pages and no pages, prefer products then literatures
          if (inSearchPages && qtyContent === 0) {
            if (qtyProducts > 0) {
              this.routing?.go({ cxRoute: 'search', params: { query: this.currentTerm } });
              return;
            }
            if (qtyLiterature > 0) {
              this.routing?.go({ cxRoute: 'searchLiterature', params: { query: this.currentTerm } });
              return;
            }
          }

          // If on literatures and no literature, prefer products then pages
          if (inSearchLiteratures && qtyLiterature === 0) {
            if (qtyProducts > 0) {
              this.routing?.go({ cxRoute: 'search', params: { query: this.currentTerm } });
              return;
            }
            if (qtyContent > 0) {
              this.routing?.go({ cxRoute: 'searchPage', params: { query: this.currentTerm } });
              return;
            }
          }
        }, 1000);
      }),
    );
  }
}
